<template>
  <Wrap>
    <div class="skeleton-gallery">
      <SkeletonItem
        v-for="tile in GALLERY_TILES_COUNT"
        :key="tile"
        class="skeleton-gallery__tile"
        border-radius="0px"
      />
    </div>
    <SkeletonItem class="skeleton-gallery__mobile" border-radius="0px" />
  </Wrap>

  <Wrap>
    <SkeletonItem class="skeleton-breadcrumbs" />
  </Wrap>

  <Wrap>
    <div class="skeleton-card">
      <section>
        <SkeletonItem class="skeleton-card__title" />

        <div class="skeleton-card__description">
          <SkeletonItem v-for="(width, index) in DESCRIPTION_LINE_WIDTHS" :key="index" :width="width" :height="22" />
        </div>

        <ul class="skeleton-card__info">
          <li v-for="item in INFO_ITEMS_COUNT" :key="item" class="skeleton-card__info-item">
            <SkeletonItem :width="20" :height="20" border-radius="50%" />
            <SkeletonItem :width="180" :height="18" />
          </li>
        </ul>
      </section>

      <Divider />

      <section>
        <SkeletonItem class="skeleton-card__subtitle" />

        <ul class="skeleton-schedule">
          <li v-for="item in SCHEDULE_ITEMS_COUNT" :key="item" class="skeleton-schedule__item">
            <SkeletonItem :width="130" :height="18" />
            <SkeletonItem class="skeleton-schedule__link" :height="18" />
          </li>
        </ul>
      </section>

      <Divider />

      <div class="skeleton-banner">
        <SkeletonItem :width="140" :height="16" />
        <SkeletonItem :width="72" :height="40" />
        <SkeletonItem :width="180" :height="16" />
        <SkeletonItem class="skeleton-banner__button" :height="44" border-radius="12px" />
      </div>

      <div class="skeleton-banner-mobile">
        <div class="skeleton-banner-mobile__text">
          <SkeletonItem :width="200" :height="16" />
          <SkeletonItem :width="130" :height="28" />
        </div>
        <SkeletonItem class="skeleton-banner-mobile__button" :height="44" border-radius="12px" />
      </div>

      <section class="skeleton-location">
        <SkeletonItem class="skeleton-card__subtitle" />
        <SkeletonItem class="skeleton-location__description" :height="18" />
        <SkeletonItem class="skeleton-location__map" border-radius="16px" />
      </section>
    </div>
  </Wrap>

  <Wrap>
    <div class="skeleton-article">
      <SkeletonItem
        v-for="(block, index) in ARTICLE_BLOCKS"
        :key="index"
        :width="block.width"
        :height="block.height"
        class="skeleton-article__block"
      />
    </div>
  </Wrap>

  <Wrap>
    <section class="skeleton-experiences">
      <SkeletonItem class="skeleton-section-title" />
      <div class="skeleton-experiences__list">
        <ExpCardSkeleton v-for="card in EXPERIENCES_COUNT" :key="card" />
      </div>
    </section>
  </Wrap>

  <Wrap>
    <section class="skeleton-nearby">
      <SkeletonItem class="skeleton-section-title" />
      <div class="skeleton-nearby__list">
        <SkeletonItem v-for="card in NEARBY_COUNT" :key="card" class="skeleton-nearby__card" border-radius="8px" />
      </div>
    </section>

    <section class="skeleton-faq">
      <SkeletonItem class="skeleton-section-title" />
      <div class="skeleton-faq__list">
        <SkeletonItem v-for="item in FAQ_ITEMS_COUNT" :key="item" :height="24" />
      </div>
    </section>
  </Wrap>
</template>

<script setup lang="ts">
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { ExpCardSkeleton } from '@experience/exp-card';

import Divider from '@/shared/ui/Divider.vue';
import Wrap from '@/shared/ui/Wrap.vue';

// Плитки повторяют раскладку horizontal-horizontal — её же PhotosGrid отдаёт по умолчанию.
const GALLERY_TILES_COUNT = 5;
const DESCRIPTION_LINE_WIDTHS = ['100%', '100%', '62%'];
const INFO_ITEMS_COUNT = 4;
const SCHEDULE_ITEMS_COUNT = 3;
const EXPERIENCES_COUNT = 6;
const NEARBY_COUNT = 6;
const FAQ_ITEMS_COUNT = 5;

const ARTICLE_BLOCKS = [
  { width: '45%', height: 32 },
  { width: '100%', height: 20 },
  { width: '100%', height: 20 },
  { width: '78%', height: 20 },
  { width: '100%', height: 20 },
  { width: '54%', height: 20 },
];
</script>

<style scoped lang="scss">
.skeleton-gallery {
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: repeat(10, 1fr);
  gap: var(--alias-space-4, 4px);
  height: 480px;
  margin-bottom: 12px;

  @include tablet {
    height: 360px;
  }

  @include mobile {
    display: none;
  }
}

.skeleton-gallery__tile {
  &:nth-child(1) {
    grid-area: 1 / 1 / 3 / 7;
  }

  &:nth-child(2) {
    grid-area: 1 / 7 / 2 / 9;
  }

  &:nth-child(3) {
    grid-area: 2 / 7 / 3 / 9;
  }

  &:nth-child(4) {
    grid-area: 1 / 9 / 2 / 11;
  }

  &:nth-child(5) {
    grid-area: 2 / 9 / 3 / 11;
  }
}

// на мобилке галерея горизонтально скроллится и выходит за поля Wrap
.skeleton-gallery__mobile {
  display: none;

  @include mobile {
    display: block;
    height: 360px;
    margin: 0 calc(-1 * var(--alias-space-20, 20px)) 12px;
  }
}

.skeleton-breadcrumbs {
  width: 260px;
  height: 34px;
  margin: 7px 1px;

  @include tablet {
    width: 100px;
  }
}

.skeleton-card {
  display: grid;
  grid-template-columns: minmax(0, 805px) minmax(300px, 1fr);
  column-gap: 40px;

  > * {
    grid-column: 1;
  }

  @include tablet {
    grid-template-columns: minmax(0, 805px);
  }
}

.skeleton-card__title {
  width: 60%;
  height: 48px;
  margin-top: 8px;

  @include tablet {
    height: 40px;
  }

  @include mobile {
    width: 100%;
    height: 28px;
  }
}

.skeleton-card__description {
  display: grid;
  gap: 8px;
  margin-top: 32px;

  @include mobile {
    margin-top: 16px;
  }
}

.skeleton-card__info {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 16px 24px;
  max-width: 571px;
  padding: 0;
  margin-top: 24px;

  @include mobile {
    grid-template-columns: 1fr;
  }
}

.skeleton-card__info-item {
  display: flex;
  gap: 8px;
  align-items: center;
  list-style-type: none;
}

.skeleton-card__subtitle {
  width: 340px;
  height: 28px;

  @include mobile {
    width: 100%;
    height: 22px;
  }
}

.skeleton-schedule {
  display: grid;
  gap: 24px;
  padding: 0;
  margin: 24px 0 0;
  list-style: none;

  @include tablet {
    gap: 16px;
    margin-top: 16px;
  }
}

.skeleton-schedule__item {
  display: flex;
  gap: 16px;
  align-items: center;

  @include mobile {
    flex-direction: column-reverse;
    gap: 8px;
    align-items: flex-start;
  }
}

.skeleton-schedule__link {
  width: 100%;
  max-width: 420px;
}

// баннер живёт во второй колонке и есть только на десктопе
.skeleton-banner {
  display: flex;
  flex-direction: column;
  grid-row: 1;
  grid-column: 2;
  gap: 4px;
  place-self: start end;
  width: 248px;

  @include tablet {
    display: none;
  }
}

.skeleton-banner__button {
  width: 100%;
  margin-top: 12px;
}

// в потоке баннер нужен там, где второй колонки нет
.skeleton-banner-mobile {
  display: none;

  @include tablet {
    box-sizing: border-box;
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 20px 24px;
    background-color: var(--alias-color-background-subtle);
    border-radius: 16px;
  }

  @include mobile {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }
}

.skeleton-banner-mobile__text {
  display: grid;
  gap: 4px;
}

.skeleton-banner-mobile__button {
  width: 170px;

  @include mobile {
    width: 100%;
  }
}

// в grid-контейнере вертикальные отступы не схлопываются, поэтому отступ уменьшен
// на margin разделителя, как и в самой карточке
.skeleton-location {
  margin-top: 24px;

  @include tablet {
    margin-top: 48px;
  }
}

.skeleton-location__description {
  width: 60%;
  margin-top: 8px;
}

.skeleton-location__map {
  width: 100%;
  height: 343px;
  margin-top: var(--alias-space-16, 16px);
}

.skeleton-article {
  display: grid;
  gap: 24px;
  max-width: var(--width-content, 805px);
  margin-top: 48px;

  @include tablet {
    gap: 16px;
    margin-top: 32px;
  }
}

.skeleton-section-title {
  width: 380px;
  height: 32px;

  @include mobile {
    width: 100%;
    height: 24px;
  }
}

.skeleton-experiences {
  margin-top: 80px;

  @include tablet {
    margin-top: 56px;
  }

  @include mobile {
    margin-top: 36px;
  }
}

.skeleton-experiences__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px 24px;
  margin-top: 24px;

  @include tablet {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include mobile {
    grid-template-columns: minmax(0, 1fr);
    gap: 26px 24px;
  }
}

.skeleton-nearby {
  margin-top: 80px;

  @include tablet {
    margin-top: 56px;
  }

  @include mobile {
    margin-top: 48px;
  }
}

// слайдер не перематывается, поэтому лишние карточки просто обрезаются
.skeleton-nearby__list {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 16px;
  margin-top: 24px;
  overflow: hidden;
}

.skeleton-nearby__card {
  height: 263px;
}

.skeleton-faq {
  margin-top: var(--alias-space-80, 80px);

  @include tablet {
    margin-top: var(--alias-space-64, 64px);
  }

  @include mobile {
    margin-top: var(--alias-space-48, 48px);
  }
}

.skeleton-faq__list {
  display: grid;
  gap: 40px;
  margin-top: 32px;
}
</style>
